import { Autocomplete, Box, Button, Card, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { makeStyles } from "@mui/styles";
import React, { useContext, useEffect, useState } from "react";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import { CourseStructureCtx } from "../../../interfaces";
import { Module as ModuleTree } from "../../utils/CourseTree";
import { Target } from "../../utils/CourseTree";
import { useHistory } from "react-router-dom";
import { CourseMetadata } from "@backend-types";

interface AssetSelectionProps {
  DeepLinkAsset: (assetId: string, assetType: string, assetName: string) => void;
  hasDeepLinked?: boolean;
}

const useStyles = makeStyles((theme) => ({
  dataTable: {
    flexGrow: 1,
    width: "100%",
    "& .MuiDataGrid-root": {
      border: "1px solid #E5E5E5",
      borderRadius: "15px",
      "& .MuiDataGrid-cell": {
        border: "1 px solid #E5E5E5 !important",
      },
      "& .MuiDataGrid-row": {
        border: "1 px solid #E5E5E5",
      },
    },
  },
  dataTableHeaderWithBorder: {
    borderBottom: "1px solid #E5E5E5",
  },
  dataTableCellWithBorder: {
    borderBottom: "1px solid #E5E5E5 !important",
  },
}));

export const AssetSelection = (props: AssetSelectionProps) => {
  const { DeepLinkAsset, hasDeepLinked } = props;
  const classes = useStyles();
  const history = useHistory();

  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const [pageSize, setPageSize] = useState<number>(10);
  const [moduleList, setModuleList] = useState<ModuleTree[]>([]);
  const [targetList, setTargetList] = useState<Target[]>([]);
  const [titleInput] = useState<string>("");
  const [useModuleDeeplink, setUseModuleDeeplink] = useState<boolean>(false);
  const [useCourseDeeplink, setUseCourseDeeplink] = useState<boolean>(false);

  useEffect(() => {
    if (!courseStructureCtx.courseTreeStructure) return;
    const moduleList = courseStructureCtx.courseTreeStructure?.getAllModulesInCourse();
    const allTargets = courseStructureCtx.courseTreeStructure?.getAllTargetsInCourse();
    const courseMetadata =
      courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata() as CourseMetadata;
    console.log("moduleList", moduleList);
    console.log("allTargets", allTargets);
    setModuleList(moduleList);
    setTargetList(allTargets);
    setUseModuleDeeplink(courseMetadata?.deeplink_asset_type === "module");
    setUseCourseDeeplink(courseMetadata?.deeplink_asset_type === "course");
  }, [courseStructureCtx.courseTreeStructure]);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: `${useModuleDeeplink ? "Module" : "Title"}`,
      flex: 1,
      cellClassName: classes.dataTableCellWithBorder,
      headerClassName: classes.dataTableHeaderWithBorder,
      renderCell: (params: any) => {
        if (useModuleDeeplink) {
          const module = params.row as ModuleTree;
          return <>{module.getFormattedModuleName()}</>;
        } else {
          const target = params.row as Target;
          return <>{target.getFormattedTargetName()}</>;
        }
      },
    },
    ...(useModuleDeeplink
      ? []
      : [
          {
            field: "module_name",
            headerName: "Module",
            flex: 1,
            cellClassName: classes.dataTableCellWithBorder,
            headerClassName: classes.dataTableHeaderWithBorder,
            renderCell: (params: any) => {
              const target = params.row as Target;
              const module = target.parent as ModuleTree;
              return <>{module.getFormattedModuleName()}</>;
            },
          },
        ]),
    {
      field: "view",
      headerName: "View",
      flex: 0.5,
      cellClassName: classes.dataTableCellWithBorder,
      headerClassName: classes.dataTableHeaderWithBorder,
      renderCell: (params) => {
        return (
          <Button
            size={"small"}
            variant="outlined"
            onClick={() => {
              if (!useModuleDeeplink) {
                let assetId = params.row.children
                  ? params.row.children.find((question: any) => {
                      return question.question_number === 1001;
                    }).id
                  : params.row.id;
                history.push({
                  pathname: "/solver",
                  search: new URLSearchParams({
                    questionId: assetId,
                  }).toString(),
                });
              } else {
                history.push({
                  pathname: "/modules",
                  search: new URLSearchParams({
                    moduleId: params.row.id,
                  }).toString(),
                });
              }
            }}
            sx={{
              borderRadius: "8px",
              font: "normal normal bold 18px/27px proxima-soft",
              "&:hover": {
                transform: "none",
              },
            }}>
            Preview
          </Button>
        );
      },
    },
    {
      field: "select",
      headerName: "Select",
      flex: 0.5,
      cellClassName: classes.dataTableCellWithBorder,
      headerClassName: classes.dataTableHeaderWithBorder,
      renderCell: (params) => {
        return (
          <Button
            size={"small"}
            variant="contained"
            disabled={hasDeepLinked}
            onClick={() => {
              if (!useModuleDeeplink) {
                let assetId = params.row.children
                  ? params.row.children.find((question: any) => {
                      return question.question_number === 1001;
                    }).id
                  : params.row.id;
                DeepLinkAsset(
                  assetId,
                  "target",
                  titleInput === "" ? params.row.name : titleInput
                );
              } else {
                DeepLinkAsset(
                  params.row.id,
                  "module",
                  titleInput === "" ? params.row.name : titleInput
                );
              }
            }}
            sx={{
              borderRadius: "8px",
              font: "normal normal bold 18px/27px proxima-soft",
              "&:hover": {
                transform: "none",
              },
            }}>
            Select
          </Button>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        padding: "32px",
        display: "flex",
        flexDirection: "column",
        alignItems: "left",
        justifyContent: "left",
        gap: "16px",
        height: "100%",
      }}>
      {!useModuleDeeplink && !useCourseDeeplink && (
        <Autocomplete
          id="combo-box-demo"
          options={moduleList}
          sx={{
            width: 406,
            "& .MuiAutocomplete-endAdornment": {
              top: "calc(50% - 25px) !important",
            },
          }}
          getOptionLabel={(option) => option.name}
          onChange={(event, value) => {
            if (!value) {
              setTargetList(
                courseStructureCtx.courseTreeStructure?.getAllTargetsInCourse() ?? []
              );
              return;
            }
            setTargetList(value?.children ?? []);
          }}
          renderInput={(params) => {
            return <TextField {...params} label="Filter by module" />;
          }}
        />
      )}
      <Card
        sx={{
          padding: "16px",
          marginTop: "8px",
          borderRadius: "15px",
          height: "auto",
          minHeight: "450px",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}>
        {useCourseDeeplink ? (
          <>
            <Button
              size={"small"}
              variant="contained"
              disabled={hasDeepLinked}
              onClick={() => {
                const course = courseStructureCtx.courseTreeStructure?.getCurrentCourse();
                if (!course || !moduleList.length || !moduleList[0]) return;
                DeepLinkAsset(
                  moduleList[0].id,
                  "course",
                  titleInput === "" ? course.name : titleInput
                );
              }}
              sx={{
                borderRadius: "8px",
                font: "normal normal bold 18px/27px proxima-soft",
                "&:hover": {
                  transform: "none",
                },
              }}>
              Select Course
            </Button>
          </>
        ) : (
          <>
            {!useModuleDeeplink && (
              <Typography variant={"h3"} sx={{ marginBottom: "16px" }}>
                Learning targets
              </Typography>
            )}
            <Box
              sx={{
                flexGrow: 1,
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}>
              <DataGrid
                disableSelectionOnClick
                disableColumnSelector
                disableDensitySelector
                disableColumnMenu
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[5, 10, 20, 100]}
                className={classes.dataTable}
                rowSpacingType={"border"}
                rows={useModuleDeeplink ? moduleList : targetList}
                columns={columns}
                autoHeight={true}
              />
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};
